import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  MapPin,
  Camera,
  Clock,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  Map,
  MoreVertical,
  TrendingUp,
} from "lucide-react";
import {
  get_student_tracking,
  get_student_detection_logs,
  get_student_location_journey,
  get_student_camera_breakdown,
  get_student_tracking_filters,
} from "services/api-service";

export default function StudentTracking() {
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  // Main tracking states
  const [selectedDate, setSelectedDate] = useState(formattedToday);
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Filter states
  const [filterOptions, setFilterOptions] = useState({
    students: [],
    sessions: [],
    cameras: [],
    locations: [],
    boarding_types: [],
    hostels: [],
  });

  const [selectedFilters, setSelectedFilters] = useState({
    student_id: "",
    student_name: "",
    session_id: "",
    camera_number: "",
    location: "",
    boarding_type: "",
    hostel_name: "",
    status: "",
  });

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  // View state - table or card
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'cards'

  // Details tab
  const [detailsTab, setDetailsTab] = useState("journey");

  // Use ref to access current filters without adding to dependencies
  const selectedFiltersRef = useRef(selectedFilters);
  
  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  // ============================================================
  // FETCH TRACKING DATA
  // ============================================================
  const fetchTrackingData = useCallback(async () => {
    if (!selectedDate) return;

    try {
      setLoading(true);
      const payload = {
        date: selectedDate,
        page_number: pageNumber,
        page_size: pageSize,
        student_id: selectedFilters.student_id || undefined,
        student_name: selectedFilters.student_name || undefined,
        session_id: selectedFilters.session_id || undefined,
        camera_number: selectedFilters.camera_number
          ? parseInt(selectedFilters.camera_number)
          : undefined,
        location: selectedFilters.location || undefined,
        boarding_type: selectedFilters.boarding_type || undefined,
        hostel_name: selectedFilters.hostel_name || undefined,
        status: selectedFilters.status || undefined,
      };

      const response = await get_student_tracking(payload);
      setTrackingData(response?.data || []);
      setTotalRecords(response?.total_records || 0);
    } catch (error) {
      console.error("Error fetching tracking data:", error);
      alert("Failed to fetch Member tracking data.");
      setTrackingData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, pageNumber, pageSize, selectedFilters]);

  // ============================================================
  // FETCH FILTER OPTIONS
  // ============================================================
  const fetchFilterOptions = useCallback(async () => {
    try {
      const payload = { date: selectedDate };
      const response = await get_student_tracking_filters(payload);
      setFilterOptions(response);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, [selectedDate]);

  // ============================================================
  // FETCH STUDENT DETAILS
  // ============================================================
  const fetchStudentDetails = useCallback(
    async (student_id) => {
      try {
        console.log("🔵 DEBUG: fetchStudentDetails called with:", student_id);
        
        // Use ref to get current filters without dependency
        const currentFilters = selectedFiltersRef.current;
        console.log("🔵 DEBUG: currentFilters:", currentFilters);
        
        setDetailsLoading(true);

        const journeyPayload = {
          student_id,
          date: selectedDate,
          camera_number: currentFilters.camera_number ? parseInt(currentFilters.camera_number) : undefined,
          location: currentFilters.location || undefined,
        };
        console.log("🔵 DEBUG: journeyPayload:", journeyPayload);
        
        const journeyResponse = await get_student_location_journey(journeyPayload);
        console.log("🔵 DEBUG: journeyResponse:", journeyResponse);

        const cameraPayload = {
          student_id,
          date: selectedDate,
          camera_number: currentFilters.camera_number ? parseInt(currentFilters.camera_number) : undefined,
          location: currentFilters.location || undefined,
        };
        
        const cameraResponse = await get_student_camera_breakdown(cameraPayload);
        console.log("🔵 DEBUG: cameraResponse:", cameraResponse);

        const logsPayload = {
          student_id,
          start_date: selectedDate,
          end_date: selectedDate,
          page_number: 1,
          page_size: 100,
          camera_number: currentFilters.camera_number ? parseInt(currentFilters.camera_number) : undefined,
          location: currentFilters.location || undefined,
        };
        
        const logsResponse = await get_student_detection_logs(logsPayload);
        console.log("🔵 DEBUG: logsResponse:", logsResponse);

        setSelectedStudentDetails({
          journey: journeyResponse,
          cameras: cameraResponse,
          logs: logsResponse,
        });
        
        console.log("🟢 DEBUG: All details loaded successfully!");
      } catch (error) {
        console.error("❌ Error fetching student details:", error);
        alert("Failed to fetch student details.");
      } finally {
        setDetailsLoading(false);
      }
    },
    [selectedDate]
  );

  // ============================================================
  // EFFECTS
  // ============================================================
  useEffect(() => {
    fetchFilterOptions();
    fetchTrackingData();
  }, [fetchTrackingData, fetchFilterOptions]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleFilterChange = (filterName, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPageNumber(1);
  };

  const handleResetFilters = () => {
    setSelectedFilters({
      student_id: "",
      student_name: "",
      session_id: "",
      camera_number: "",
      location: "",
      boarding_type: "",
      hostel_name: "",
      status: "",
    });
    setPageNumber(1);
  };

  const handleExpandStudent = useCallback((student) => {
    if (expandedStudent === student.student_id) {
      setExpandedStudent(null);
      setSelectedStudentDetails(null);
    } else {
      setExpandedStudent(student.student_id);
      setDetailsLoading(true);
      fetchStudentDetails(student.student_id);
    }
  }, [expandedStudent, fetchStudentDetails]);

  // ============================================================
  // RENDER: DETECTION LOGS TABLE
  // ============================================================
  const renderDetectionLogsTable = (student) => {
    if (!student || !student.detection_logs || student.detection_logs.length === 0) {
      return <div style={{ color: "#666", padding: "12px" }}>No detection logs</div>;
    }

    return (
      <div style={{ overflowX: "auto", marginTop: "12px" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "12px",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>
                #
              </th>
              <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>
                Timestamp
              </th>
              <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>
                Camera
              </th>
              <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>
                Location
              </th>
              <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>
                Confidence
              </th>
            </tr>
          </thead>
          <tbody>
            {student.detection_logs.map((log, idx) => (
              <tr
                key={log.id}
                style={{
                  borderBottom: "1px solid #e5e7eb",
                  backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb",
                }}
              >
                <td style={{ padding: "8px" }}>{log.sequence}</td>
                <td style={{ padding: "8px" }}>
                  {log.timestamp
                    ? new Date(log.timestamp).toLocaleTimeString()
                    : "—"}
                </td>
                <td style={{ padding: "8px" }}>
                  📹 {log.camera_name || log.camera_number}
                </td>
                <td style={{ padding: "8px" }}>📍 {log.location || "—"}</td>
                <td style={{ padding: "8px" }}>
                  {log.confidence ? (log.confidence * 100).toFixed(1) + "%" : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ============================================================
  // RENDER: CAMERA BREAKDOWN
  // ============================================================
  const renderCameraBreakdown = (cameraData) => {
    if (!cameraData || !cameraData.camera_breakdown) {
      return <div style={{ color: "#666" }}>No camera data</div>;
    }

    return (
      <div style={{ marginTop: "12px" }}>
        {cameraData.camera_breakdown.map((cam) => (
          <div
            key={cam.camera_number}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              borderRadius: "4px",
              marginBottom: "8px",
            }}
          >
            <div>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#1a1a1a" }}>
                📹 {cam.camera_name || `Camera ${cam.camera_number}`}
              </div>
              <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                📍 {cam.location || "—"}
              </div>
            </div>
            <div style={{ textAlign: "right", marginLeft: "auto", paddingLeft: "20px" }}>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#4f46e5", marginBottom: "2px" }}>
                {cam.detection_count}
              </div>
              <div style={{ fontSize: "10px", color: "#666", marginBottom: "2px" }}>
                Detections
              </div>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#f59e0b" }}>
                {cam.detection_percentage}%
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============================================================
  // RENDER: LOCATION JOURNEY
  // ============================================================
  const renderLocationJourney = (journeyData) => {
    if (!journeyData || !journeyData.journey || journeyData.journey.length === 0) {
      return <div style={{ color: "#666" }}>No location journey data</div>;
    }

    return (
      <div style={{ marginTop: "12px", maxHeight: "400px", overflowY: "auto" }}>
        {journeyData.journey.map((entry, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "8px",
              paddingBottom: "8px",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "28px",
                backgroundColor: "#4f46e5",
                color: "white",
                borderRadius: "50%",
                fontSize: "11px",
                fontWeight: "600",
                flexShrink: 0,
              }}
            >
              {idx + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#1a1a1a" }}>
                📹 {entry.camera_name || `Camera ${entry.camera_number}`}
              </div>
              <div style={{ fontSize: "11px", color: "#666", marginTop: "1px" }}>
                📍 {entry.location || "—"}
              </div>
              <div style={{ fontSize: "11px", color: "#666", marginTop: "1px" }}>
                🕐{" "}
                {entry.timestamp
                  ? new Date(entry.timestamp).toLocaleTimeString()
                  : "—"}
              </div>
              {entry.time_since_previous_minutes !== null && (
                <div
                  style={{ fontSize: "10px", color: "#10b981", marginTop: "1px" }}
                >
                  ⏱️ {entry.time_since_previous_minutes} min since previous
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============================================================
  // RENDER: STATISTICS CARDS
  // ============================================================
  const renderStatisticsCards = () => {
    const summary = trackingData.reduce(
      (acc, student) => ({
        totalDetections: acc.totalDetections + student.total_detections,
        uniqueCameras: Math.max(acc.uniqueCameras, student.unique_cameras),
      }),
      { totalDetections: 0, uniqueCameras: 0 }
    );

    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <StatCard icon="👥" label="Students Tracked" value={trackingData.length} color="#4f46e5" />
        <StatCard icon="📹" label="Total Detections" value={summary.totalDetections} color="#f59e0b" />
        <StatCard icon="🎥" label="Active Cameras" value={summary.uniqueCameras} color="#10b981" />
      </div>
    );
  };

  const StatCard = ({ icon, label, value, color }) => (
    <div style={{
      backgroundColor: "white",
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
    }}>
      <div style={{ fontSize: "24px", marginBottom: "8px" }}>{icon}</div>
      <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>{label}</div>
      <div style={{ fontSize: "32px", fontWeight: "700", color }}>{value}</div>
    </div>
  );

  // ============================================================
  // RENDER: TABLE VIEW (for many students) - FIXED
  // ============================================================
  const renderTableView = () => {
    return (
      <div style={{ backgroundColor: "white", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", overflow: "hidden", marginBottom: "20px" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                <th style={tableHeaderStyle}>Student Name</th>
                <th style={tableHeaderStyle}>Student ID</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>First Seen</th>
                <th style={tableHeaderStyle}>Last Seen</th>
                <th style={tableHeaderStyle}>Detections</th>
                <th style={tableHeaderStyle}>Cameras</th>
                <th style={tableHeaderStyle}>Boarding</th>
                <th style={tableHeaderStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {trackingData.map((student, idx) => (
                <React.Fragment key={student.student_id}>
                  <tr
                    style={{
                      borderBottom: expandedStudent === student.student_id ? "none" : "1px solid #e5e7eb",
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb",
                    }}
                  >
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: "600", color: "#1a1a1a" }}>
                        {student.student_name}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {student.student_id}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={getStatusBadgeStyle(student.status)}>
                        {student.status}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{ fontSize: "13px", color: "#666" }}>
                        {student.first_detection_time
                          ? new Date(student.first_detection_time).toLocaleTimeString()
                          : "—"}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{ fontSize: "13px", color: "#666" }}>
                        {student.last_detection_time
                          ? new Date(student.last_detection_time).toLocaleTimeString()
                          : "—"}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#4f46e5" }}>
                        {student.total_detections}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#f59e0b" }}>
                        {student.unique_cameras}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {student.boarding_type === "hostel" ? "🏠 Hostel" : "🎓 Day"}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <button
                        onClick={() => handleExpandStudent(student)}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: expandedStudent === student.student_id ? "#4f46e5" : "#f0f0f0",
                          color: expandedStudent === student.student_id ? "white" : "#333",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {expandedStudent === student.student_id ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Details Row - INLINE */}
                  {expandedStudent === student.student_id && (
                    <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                      <td colSpan="9" style={{ padding: "20px" }}>
                        {detailsLoading ? (
                          <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
                            Loading details...
                          </div>
                        ) : selectedStudentDetails ? (
                          <div>
                            <h3 style={{ marginTop: "0", marginBottom: "16px", color: "#1a1a1a" }}>
                              Details: {student.student_name}
                            </h3>

                            {/* Tabs */}
                            <div style={{ display: "flex", gap: "12px", marginBottom: "20px", borderBottom: "2px solid #e5e7eb", paddingBottom: "12px" }}>
                              <button
                                onClick={() => setDetailsTab("journey")}
                                style={{
                                  padding: "8px 16px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  fontWeight: detailsTab === "journey" ? "600" : "500",
                                  color: detailsTab === "journey" ? "#4f46e5" : "#6b7280",
                                  borderBottom: detailsTab === "journey" ? "3px solid #4f46e5" : "none",
                                }}
                              >
                                🗺️ Location Journey
                              </button>
                              <button
                                onClick={() => setDetailsTab("cameras")}
                                style={{
                                  padding: "8px 16px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  fontWeight: detailsTab === "cameras" ? "600" : "500",
                                  color: detailsTab === "cameras" ? "#4f46e5" : "#6b7280",
                                  borderBottom: detailsTab === "cameras" ? "3px solid #4f46e5" : "none",
                                }}
                              >
                                📹 Camera Breakdown
                              </button>
                              <button
                                onClick={() => setDetailsTab("logs")}
                                style={{
                                  padding: "8px 16px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  fontWeight: detailsTab === "logs" ? "600" : "500",
                                  color: detailsTab === "logs" ? "#4f46e5" : "#6b7280",
                                  borderBottom: detailsTab === "logs" ? "3px solid #4f46e5" : "none",
                                }}
                              >
                                📋 Detection Logs
                              </button>
                            </div>

                            {/* Tab Content */}
                            {detailsTab === "journey" && renderLocationJourney(selectedStudentDetails.journey)}
                            {detailsTab === "cameras" && renderCameraBreakdown(selectedStudentDetails.cameras)}
                            {detailsTab === "logs" && renderDetectionLogsTable(student)}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER: CARD VIEW (for smaller datasets)
  // ============================================================
  const renderCardView = () => {
    return (
      <div>
        {trackingData.map((student) => renderStudentCard(student))}
      </div>
    );
  };

  const renderStudentCard = (student) => {
    const isExpanded = expandedStudent === student.student_id;

    return (
      <div
        key={student.student_id}
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid #f0f0f0",
          overflow: "hidden",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            padding: "20px",
            borderBottom: isExpanded ? "1px solid #e5e7eb" : "none",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          onClick={() => handleExpandStudent(student)}
        >
          <div>
            <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>
              {student.student_name}
            </div>
            <div style={{ fontSize: "12px", opacity: "0.9" }}>
              ID: {student.student_id}
            </div>
          </div>
          <div style={{ textAlign: "right", display: "flex", alignItems: "center", gap: "16px" }}>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "600" }}>
                {student.total_detections}
              </div>
              <div style={{ fontSize: "11px", opacity: "0.8" }}>detections</div>
            </div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>

        {isExpanded && selectedStudentDetails && !detailsLoading && (
          <div style={{ padding: "20px", backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "12px", marginBottom: "20px" }}>
              <DetailItem label="First Seen" value={student.first_detection_time ? new Date(student.first_detection_time).toLocaleTimeString() : "—"} />
              <DetailItem label="Last Seen" value={student.last_detection_time ? new Date(student.last_detection_time).toLocaleTimeString() : "—"} />
              <DetailItem label="Status" value={student.status} />
              <DetailItem label="Duration" value={`${student.total_duration_minutes} min`} />
            </div>

            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "16px" }}>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "8px" }}>
                Camera Sequence:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {student.cameras_sequence.map((cam, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: "inline-block",
                      padding: "4px 8px",
                      backgroundColor: "#dbeafe",
                      color: "#1e40af",
                      borderRadius: "4px",
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    {idx + 1}. {cam}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {isExpanded && detailsLoading && (
          <div style={{ padding: "20px", color: "#666", textAlign: "center" }}>
            Loading details...
          </div>
        )}
      </div>
    );
  };

  const DetailItem = ({ label, value }) => (
    <div>
      <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "13px", fontWeight: "600", color: "#1a1a1a" }}>{value}</div>
    </div>
  );

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================
  const tableHeaderStyle = {
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "12px",
    color: "#374151",
    whiteSpace: "nowrap",
  };

  const tableCellStyle = {
    padding: "12px 16px",
    fontSize: "13px",
  };

  const getStatusBadgeStyle = (status) => ({
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    backgroundColor:
      status === "Present"
        ? "#dcfce7"
        : status === "Late"
        ? "#fef08a"
        : "#fee2e2",
    color:
      status === "Present"
        ? "#166534"
        : status === "Late"
        ? "#713f12"
        : "#991b1b",
  });

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "32px", fontWeight: "700", color: "#1a1a1a", margin: "0 0 20px 0" }}>
          📍 Student Location Tracking traking trankingn
        </h1>

        {/* Controls Row */}
        <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" }}>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPageNumber(1);
            }}
            style={{
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid #d0d0d0",
              fontSize: "14px",
            }}
          />
          <button
            onClick={fetchTrackingData}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4f46e5",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <RefreshCw size={16} /> Search
          </button>

          {/* View Mode Toggle */}
          <div style={{ display: "flex", gap: "6px", marginLeft: "auto" }}>
            <button
              onClick={() => setViewMode("table")}
              style={{
                padding: "8px 16px",
                backgroundColor: viewMode === "table" ? "#4f46e5" : "#e5e7eb",
                color: viewMode === "table" ? "white" : "#374151",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              📊 Table
            </button>
            <button
              onClick={() => setViewMode("cards")}
              style={{
                padding: "8px 16px",
                backgroundColor: viewMode === "cards" ? "#4f46e5" : "#e5e7eb",
                color: viewMode === "cards" ? "white" : "#374151",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              🎴 Cards
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={selectedFilters.student_name}
            onChange={(e) => handleFilterChange("student_name", e.target.value)}
            style={{
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid #d0d0d0",
              fontSize: "14px",
              minWidth: "140px",
            }}
          >
            <option value="">All Students</option>
            {filterOptions.students.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            value={selectedFilters.camera_number}
            onChange={(e) => handleFilterChange("camera_number", e.target.value)}
            style={{
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid #d0d0d0",
              fontSize: "14px",
              minWidth: "140px",
            }}
          >
            <option value="">All Cameras</option>
            {filterOptions.cameras && filterOptions.cameras.length > 0 ? (
              filterOptions.cameras.map((c) => (
                <option key={c.number} value={c.number}>
                  📹 {c.name || `Camera ${c.number}`}
                </option>
              ))
            ) : (
              <option disabled>No cameras available</option>
            )}
          </select>

          <select
            value={selectedFilters.location}
            onChange={(e) => handleFilterChange("location", e.target.value)}
            style={{
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px solid #d0d0d0",
              fontSize: "14px",
              minWidth: "140px",
            }}
          >
            <option value="">All Locations</option>
            {filterOptions.locations && filterOptions.locations.length > 0 ? (
              filterOptions.locations.map((l) => (
                <option key={typeof l === 'string' ? l : l.name} value={typeof l === 'string' ? l : l.name}>
                  📍 {typeof l === 'string' ? l : l.name}
                </option>
              ))
            ) : (
              <option disabled>No locations available</option>
            )}
          </select>

          <button
            onClick={handleResetFilters}
            style={{
              padding: "10px 16px",
              backgroundColor: "#6b7280",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            ↺ Reset
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {!loading && trackingData.length > 0 && renderStatisticsCards()}

      {/* Content Area */}
      {!loading && trackingData.length > 0 && (
        viewMode === "table" ? renderTableView() : renderCardView()
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#666" }}>
          <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "8px" }}>
            Loading tracking data...
          </div>
          <div style={{ fontSize: "13px", opacity: "0.7" }}>Please wait</div>
        </div>
      )}

      {/* Empty State */}
      {!loading && trackingData.length === 0 && (
        <div
          style={{
            backgroundColor: "white",
            padding: "60px 20px",
            borderRadius: "12px",
            textAlign: "center",
            color: "#999",
          }}
        >
          <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "8px" }}>
            No records found
          </div>
          <div style={{ fontSize: "13px" }}>
            No Member tracking records found for {selectedDate}
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && trackingData.length > 0 && totalRecords > pageSize && (
        <div style={{ marginTop: "24px", textAlign: "center" }}>
          <button
            onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
            disabled={pageNumber === 1}
            style={{
              marginRight: "12px",
              padding: "10px 16px",
              backgroundColor: pageNumber === 1 ? "#ccc" : "#4f46e5",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: pageNumber === 1 ? "default" : "pointer",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            ← Previous
          </button>
          <span style={{ marginRight: "12px", fontSize: "14px", color: "#666", fontWeight: "600" }}>
            Page {pageNumber} of {Math.ceil(totalRecords / pageSize)}
          </span>
          <button
            onClick={() => setPageNumber(pageNumber + 1)}
            disabled={pageNumber * pageSize >= totalRecords}
            style={{
              padding: "10px 16px",
              backgroundColor:
                pageNumber * pageSize >= totalRecords ? "#ccc" : "#4f46e5",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor:
                pageNumber * pageSize >= totalRecords ? "default" : "pointer",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}